import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiClient } from "@/common/apiClient";

interface Question {
  id: string;
  text: string;
  order: number;
  questionListId?: string;
  sourceListId?: string;
}

interface QuestionListMetadata {
  questionListId: string;
  title: string;
  order: number;
}

interface QuestionList {
  _id: string;
  title: string;
  description: string;
  questions: Question[];
}

interface KPIForm {
  _id: string;
  title: string;
  description: string;
  questionLists: QuestionListMetadata[];
  questions: Question[];
  status: string;
  lastSyncedAt: string;
}

export const useUpdateKPIForm = (id: string | undefined) => {
  const navigate = useNavigate();

  const [form, setForm] = useState<KPIForm | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [division, setDivision] = useState("");
  const [selectedQuestionLists, setSelectedQuestionLists] = useState<QuestionListMetadata[]>([]);
  const [availableQuestionLists, setAvailableQuestionLists] = useState<QuestionList[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLists, setIsLoadingLists] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const response = await apiClient.get(`/api/kpi-form/${id}`);

        if (response.success) {
          const data = response.data;
          setForm(data);
          setTitle(data.title || "");
          setDescription(data.description || "");
          setDivision(data.division || "");
          setSelectedQuestionLists(data.questionLists || []);
        } else {
          toast.error("Failed to load KPI form");
          navigate("/view-all-kpi-form");
        }
      } catch (error) {
        toast.error("Failed to load KPI form: " + (error as any).message);
        navigate("/view-all-kpi-form");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchForm();
    }
  }, [id, navigate]);

  useEffect(() => {
    const fetchQuestionLists = async () => {
      try {
        const response = await apiClient.get("/api/kpi-question-list");
        if (response.success) {
          setAvailableQuestionLists(response.data || []);
        } else {
          toast.error("Failed to load question lists");
        }
      } catch (error) {
        toast.error("Failed to load question lists: " + (error as any).message);
      } finally {
        setIsLoadingLists(false);
      }
    };

    fetchQuestionLists();
  }, []);

  const handleQuestionListToggle = (questionListId: string) => {
    const list = availableQuestionLists.find((ql) => ql._id === questionListId);
    if (!list) return;

    const isSelected = selectedQuestionLists.some(
      (sql) => sql.questionListId === questionListId
    );

    if (isSelected) {
      setSelectedQuestionLists((prev) =>
        prev.filter((sql) => sql.questionListId !== questionListId)
      );
    } else {
      setSelectedQuestionLists((prev) => [
        ...prev,
        {
          questionListId: list._id,
          title: list.title,
          order: prev.length + 1,
        },
      ]);
    }
  };

  const moveQuestionListUp = (index: number) => {
    if (index === 0) return;

    const newLists = [...selectedQuestionLists];
    [newLists[index - 1], newLists[index]] = [
      newLists[index],
      newLists[index - 1],
    ];

    newLists.forEach((ql, i) => {
      ql.order = i + 1;
    });

    setSelectedQuestionLists(newLists);
  };

  const moveQuestionListDown = (index: number) => {
    if (index === selectedQuestionLists.length - 1) return;

    const newLists = [...selectedQuestionLists];
    [newLists[index], newLists[index + 1]] = [
      newLists[index + 1],
      newLists[index],
    ];

    newLists.forEach((ql, i) => {
      ql.order = i + 1;
    });

    setSelectedQuestionLists(newLists);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const isDraft = form?.status === "draft";

    try {
      if (isDraft && selectedQuestionLists.length === 0) {
        toast.error("Please select at least one question list");
        return;
      }

      const updateData: any = {
        title,
        description,
        division,
      };

      if (isDraft) {
        updateData.questionLists = selectedQuestionLists.map((sql) => ({
          questionListId: sql.questionListId,
          order: sql.order,
        }));
      }

      const response = await apiClient.put(`/api/kpi-form/${id}`, updateData);

      if (response.success) {
        toast.success("KPI Form updated successfully!");
        navigate("/view-all-kpi-form");
      } else {
        toast.error(
          "Failed to update KPI form: " + (response.message || "Unknown error")
        );
      }
    } catch (error) {
      toast.error("Failed to update KPI form: " + (error as any).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    title,
    setTitle,
    description,
    setDescription,
    division,
    setDivision,
    selectedQuestionLists,
    availableQuestionLists,
    isLoading,
    isLoadingLists,
    isSubmitting,
    handleQuestionListToggle,
    moveQuestionListUp,
    moveQuestionListDown,
    handleSubmit,
  };
};
